import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Layers, Plus, Pencil } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  asset_class: '', description: '',
  asset_gl_account: '', accumulated_depreciation_gl_account: '',
  depreciation_expense_gl_account: '', gain_loss_gl_account: '', clearing_gl_account: '',
  default_useful_life_years: 5, default_depreciation_method: 'STRAIGHT_LINE', status: 'Active',
};

const GL_FIELDS = [
  ['asset_gl_account', 'Asset (APC) G/L'],
  ['accumulated_depreciation_gl_account', 'Accumulated Depreciation G/L'],
  ['depreciation_expense_gl_account', 'Depreciation Expense G/L'],
  ['gain_loss_gl_account', 'Gain/Loss on Disposal G/L'],
  ['clearing_gl_account', 'Acquisition Clearing G/L'],
];

const AssetClasses = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('asset_class');
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assets/asset-classes`, { withCredentials: true });
      setClasses(res.data);
    } catch (e) {
      toast.error('Failed to load asset classes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openNew = () => { setForm(EMPTY); setEditing(false); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditing(true); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/assets/asset-classes/${form.asset_class}`, form, { withCredentials: true });
        toast.success('Asset class updated');
      } else {
        await axios.post(`${API}/assets/asset-classes`, form, { withCredentials: true });
        toast.success('Asset class created');
      }
      setOpen(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save asset class');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="asset-classes-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Layers className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asset Classes</h1>
              <p className="text-gray-600 text-sm mt-0.5">Account determination & depreciation defaults per class.</p>
            </div>
          </div>
          {writable && (
            <Button onClick={openNew} data-testid="new-asset-class-btn"><Plus className="h-4 w-4 mr-2" />New Asset Class</Button>
          )}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>APC G/L</TableHead>
                <TableHead>Accum Dep G/L</TableHead>
                <TableHead>Life (yrs)</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
              ) : classes.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No asset classes yet.</TableCell></TableRow>
              ) : classes.map((c) => (
                <TableRow key={c.asset_class} data-testid={`asset-class-row-${c.asset_class}`}>
                  <TableCell className="font-medium">{c.asset_class}</TableCell>
                  <TableCell>{c.description}</TableCell>
                  <TableCell className="font-mono text-xs">{c.asset_gl_account}</TableCell>
                  <TableCell className="font-mono text-xs">{c.accumulated_depreciation_gl_account}</TableCell>
                  <TableCell>{c.default_useful_life_years}</TableCell>
                  <TableCell className="text-xs">{c.default_depreciation_method}</TableCell>
                  <TableCell><Badge variant={c.status === 'Active' ? 'default' : 'secondary'}>{c.status}</Badge></TableCell>
                  <TableCell>
                    {writable && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-asset-class-${c.asset_class}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} Asset Class</DialogTitle>
            <DialogDescription>Define the G/L accounts and depreciation defaults used by assets in this class.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Asset Class Code</Label>
              <Input data-testid="ac-code" value={form.asset_class} disabled={editing} maxLength={12}
                onChange={(e) => set('asset_class', e.target.value.toUpperCase())} placeholder="MACH" />
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="ac-description" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            {GL_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input data-testid={`ac-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder="e.g. 160000" />
              </div>
            ))}
            <div>
              <Label>Default Useful Life (years)</Label>
              <Input type="number" min={1} max={100} data-testid="ac-life" value={form.default_useful_life_years}
                onChange={(e) => set('default_useful_life_years', parseInt(e.target.value, 10) || 1)} />
            </div>
            <div>
              <Label>Depreciation Method</Label>
              <Select value={form.default_depreciation_method} onValueChange={(v) => set('default_depreciation_method', v)}>
                <SelectTrigger data-testid="ac-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="STRAIGHT_LINE">Straight Line</SelectItem>
                  <SelectItem value="DECLINING_BALANCE">Declining Balance</SelectItem>
                  <SelectItem value="NONE">No Depreciation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.asset_class || !form.description} data-testid="save-asset-class-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AssetClasses;
